import LeadFormModal from "@/components/LeadFormModal";
import BulkQueryImportModal from "@/components/BulkQueryImportModal";
import { useEffect, useState } from "react";
import useResponsive from "@/hooks/useResponsive";
import ResponsivePage from "@/components/layout/ResponsivePage";
import { useNavigate, useLocation } from "react-router-dom";
import apiClient from "@/utils/apiClient";
import { Button, Space, Tag } from "antd";
import {
  PlusOutlined,
  UploadOutlined,
  FilterOutlined,
  ClearOutlined,
} from "@ant-design/icons";

const TripsPage = () => {

  const navigate = useNavigate();

const { isMobile } = useResponsive();

  const location = useLocation();

  const queryParams =
    new URLSearchParams(
      location.search
    );

  const statusFilter =
    queryParams.get("status");

  const [
    showImportModal,
    setShowImportModal,
  ] = useState(false);

  const [
    showLeadModal,
    setShowLeadModal,
  ] = useState(false);

  const [trips, setTrips] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [search, setSearch] =
    useState("");

  const [
    showDropdown,
    setShowDropdown,
  ] = useState(false);

  const [
    debouncedSearch,
    setDebouncedSearch,
  ] = useState("");

  useEffect(() => {

    fetchTrips();

  }, []);

  /* =========================================
     FETCH LEADS
  ========================================= */

  const fetchTrips = async () => {

    try {

      const res =
        await apiClient.get(
          `/leads`
        );

setTrips(
  Array.isArray(res.data?.data)
    ? res.data.data
    : []
);

    } catch (err) {

      console.error(
        "Error fetching leads:",
        err
      );

    } finally {

      setLoading(false);
    }
  };

  /* =========================================
     SEARCH DEBOUNCE
  ========================================= */

  useEffect(() => {

    const timer =
      setTimeout(() => {

        setDebouncedSearch(
          search
        );

      }, 300);

    return () =>
      clearTimeout(timer);

  }, [search]);

  /* =========================================
     FILTERING & URL PARAM PARSING
  ========================================= */

const activeFilterParams = (() => {
  const params = new URLSearchParams(location.search);
  const activeObj = {};
  params.forEach((val, key) => {
    if (val) activeObj[key] = val;
  });
  return activeObj;
})();

const filteredTrips = (() => {
  const query = debouncedSearch.trim().toLowerCase();

  const getDestination = (trip) =>
    String(
      trip.destinationSnapshot ||
      trip.destination?.name ||
      (typeof trip.destination === "string" ? trip.destination : "")
    ).toLowerCase();

  const getScore = (trip) => {
    if (!query) return 1;

    let score = 0;
    const phone = String(trip.phone || "").toLowerCase();
    const leadId = String(trip.leadId || "").toLowerCase();
    const mongoId = String(trip._id || "").toLowerCase();
    const name = String(trip.customerName || "").toLowerCase();
    const email = String(trip.email || "").toLowerCase();
    const destination = getDestination(trip);

    if (phone === query) score += 1000;
    else if (phone.startsWith(query)) score += 900;
    else if (phone.includes(query)) score += 800;

    if (leadId === query) score += 700;
    else if (leadId.startsWith(query)) score += 650;
    else if (leadId.includes(query)) score += 600;

    if (mongoId === query) score += 550;
    else if (mongoId.startsWith(query)) score += 500;
    else if (mongoId.includes(query)) score += 450;

    if (name.startsWith(query)) score += 400;
    else if (name.includes(query)) score += 350;

    if (destination.startsWith(query)) score += 300;
    else if (destination.includes(query)) score += 250;

    if (email.startsWith(query)) score += 200;
    else if (email.includes(query)) score += 150;

    return score;
  };

  const statusVal = activeFilterParams.status;
  const priorityVal = activeFilterParams.priority;
  const destVal = activeFilterParams.destinations || activeFilterParams.destination;
  const sourceVal = activeFilterParams.tripSources || activeFilterParams.source;
  const tagsVal = activeFilterParams.tags;
  const minAmt = Number(activeFilterParams.minAmount || activeFilterParams.minBudget);
  const maxAmt = Number(activeFilterParams.maxAmount || activeFilterParams.maxBudget);
  const tripIdVal = (activeFilterParams.tripId || activeFilterParams.leadId || "").toLowerCase();
  const customerVal = (activeFilterParams.customerName || "").toLowerCase();
  const phoneVal = (activeFilterParams.phoneNumber || "").toLowerCase();

  return trips
    .map((trip) => ({
      trip,
      score: getScore(trip),
    }))
    .filter(({ trip, score }) => {
      if (query && score === 0) return false;

      // Status filter
      if (statusVal && statusVal !== "all") {
        const list = statusVal.toLowerCase().split(",").map((s) => s.trim());
        const tripStatus = String(trip.status || "").toLowerCase();
        if (!list.includes(tripStatus)) return false;
      }

      // Priority filter
      if (priorityVal && priorityVal !== "all") {
        const list = priorityVal.toLowerCase().split(",").map((s) => s.trim());
        const tripPriority = String(trip.priority || "").toLowerCase();
        if (!list.includes(tripPriority)) return false;
      }

      // Destination filter
      if (destVal) {
        const list = destVal.toLowerCase().split(",").map((s) => s.trim());
        const dName = getDestination(trip);
        if (!list.some((d) => dName.includes(d))) return false;
      }

      // Source filter
      if (sourceVal && sourceVal !== "all") {
        const list = sourceVal.toLowerCase().split(",").map((s) => s.trim());
        const tripSource = String(trip.source || "").toLowerCase();
        if (!list.includes(tripSource)) return false;
      }

      // Tags filter
      if (tagsVal) {
        const list = tagsVal.toLowerCase().split(",").map((s) => s.trim());
        const tripTags = Array.isArray(trip.tags)
          ? trip.tags.map((t) => String(t).toLowerCase())
          : [];
        if (!list.some((t) => tripTags.includes(t))) return false;
      }

      // Min/Max Amount filter
      const tripMinBudget = trip.budget?.min || trip.budget?.max || trip.quotedAmount || 0;
      const tripMaxBudget = trip.budget?.max || trip.budget?.min || trip.quotedAmount || 0;
      if (!isNaN(minAmt) && minAmt > 0 && tripMaxBudget < minAmt) return false;
      if (!isNaN(maxAmt) && maxAmt > 0 && tripMinBudget > maxAmt) return false;

      // Trip ID / Lead ID filter
      if (tripIdVal) {
        const lId = String(trip.leadId || "").toLowerCase();
        const mId = String(trip._id || "").toLowerCase();
        if (!lId.includes(tripIdVal) && !mId.includes(tripIdVal)) return false;
      }

      // Customer Name filter
      if (customerVal) {
        const cName = String(trip.customerName || "").toLowerCase();
        if (!cName.includes(customerVal)) return false;
      }

      // Phone Number filter
      if (phoneVal) {
        const cPhone = String(trip.phone || "").toLowerCase();
        if (!cPhone.includes(phoneVal)) return false;
      }

      return true;
    })
    .sort((a, b) => b.score - a.score)
    .map(({ trip }) => trip);
})();

return (

<ResponsivePage
  title="Leads"
  subtitle="Manage travel inquiries, follow-ups & conversions"
  fullWidth
  actions={
    <Space>
      <Button
        icon={<UploadOutlined />}
        onClick={() => setShowImportModal(true)}
      >
        Import
      </Button>

      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => setShowLeadModal(true)}
      >
        Add Query
      </Button>
    </Space>
  }
>

      {/* =====================================
          SEARCH
      ===================================== */}

      <div
        style={{
          width: "100%",
          maxWidth: isMobile ? "100%" : 520,
          position: "relative",
          marginBottom: 16,
        }}
      >

        <span
          style={{
            position: "absolute",
            left: 14,
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: 14,
            color: "#94a3b8",
            pointerEvents: "none",
          }}
        >
          🔍
        </span>

        <input
          type="text"
          placeholder="Search customer, destination, phone, lead ID..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
          style={{
            width: "100%",
            height: 42,
            padding: "0 14px 0 40px",
            borderRadius: 12,
            border: "1px solid #e2e8f0",
            fontSize: 13,
            background: "#ffffff",
            color: "#0f172a",
            outline: "none",
            boxShadow: "0 1px 3px rgba(15,23,42,0.03)",
            transition: "all 0.2s ease",
          }}
        />

        {/* SEARCH DROPDOWN */}

        {showDropdown

          &&

          debouncedSearch && (

            <div
              style={{
                position:
                  "absolute",
                top: 44,
                left: 0,
                width: "100%",
                background:
                  "#fff",
                border:
                  "1px solid #e5e7eb",
                borderRadius: 8,
                boxShadow:
                  "0 4px 12px rgba(0,0,0,0.08)",
                zIndex: 20,
                maxHeight: 260,
                overflowY: "auto",
              }}
            >

              {filteredTrips
                .slice(0, 6)
                .map((trip) => (

                  <div
                    key={trip._id}
                    onClick={() =>
                      navigate(
                        `/trips/${trip._id}`
                      )
                    }
                    style={{
                      padding:
                        "10px 12px",
                      borderBottom:
                        "1px solid #f3f4f6",
                      cursor:
                        "pointer",
                    }}
                  >

                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                      }}
                    >
                      {trip.customerName ||
                        "Unnamed"}
                    </div>

                    <div
                      style={{
                        fontSize: 12,
                        color:
                          "#6b7280",
                        marginTop: 2,
                      }}
                    >
                      {trip.phone ||
                        "-"}{" "}

                      •

                      {" "}

{
  trip.destinationSnapshot ||
  trip.destination?.name ||
  (
    typeof trip.destination === "string"
      ? trip.destination
      : "-"
  )
}

                    </div>

                  </div>
                ))}

              {filteredTrips.length ===
                0 && (

                  <div
                    style={{
                      padding: 12,
                      fontSize: 13,
                      color:
                        "#9ca3af",
                    }}
                  >
                    No results found
                  </div>
                )}

            </div>
          )}

      </div>

      {/* =====================================
          ACTIVE FILTERS CHIPS
      ===================================== */}
      {Object.keys(activeFilterParams).length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
            marginBottom: 16,
            padding: "10px 14px",
            background: "#f8fafc",
            borderRadius: 8,
            border: "1px solid #e2e8f0",
          }}
        >
          <Space align="center" size={4} style={{ marginRight: 6 }}>
            <FilterOutlined style={{ color: "#4f46e5" }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: "#475569" }}>
              Active Filters:
            </span>
          </Space>

          {Object.entries(activeFilterParams).map(([key, value]) => (
            <Tag
              key={key}
              closable
              color="indigo"
              onClose={() => {
                const params = new URLSearchParams(location.search);
                params.delete(key);
                const q = params.toString();
                navigate(q ? `/leads?${q}` : "/leads");
              }}
              style={{
                borderRadius: 6,
                padding: "2px 8px",
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              <strong style={{ textTransform: "capitalize" }}>{key}:</strong> {String(value)}
            </Tag>
          ))}

          <Button
            type="link"
            size="small"
            icon={<ClearOutlined />}
            onClick={() => navigate("/leads")}
            style={{ color: "#ef4444", fontSize: 12, paddingLeft: 4 }}
          >
            Clear All
          </Button>
        </div>
      )}

      {/* =====================================
          META
      ===================================== */}

      <div
        style={{
          fontSize: 13,
          color: "#666",
          marginBottom: 12,
        }}
      >
        Showing{" "}

        <strong>
          {filteredTrips.length}
        </strong>{" "}

        items
      </div>

      {/* =====================================
          TABLE HEADER
      ===================================== */}

<div
style={{
overflowX:"auto",
}}
>

<div
style={{
display:"grid",
gridTemplateColumns:
"90px 240px 180px 140px 120px 140px 180px 130px 220px",

minWidth:1440,

          padding: "12px",

          fontSize: 13,

          color: "#6b7280",

          borderBottom:
            "1px solid #e5e7eb",

          background: "#fafafa",

          fontWeight: 600,
        }}
      >

        <div>Lead ID</div>

        <div>Customer</div>

        <div>Destination</div>

        <div>Pax</div>

        <div>Status</div>

        <div>Advisor</div>

        <div>Tags</div>

        <div>Travel Date</div>

        <div>Last Follow Up</div>

      </div>
     

      {/* =====================================
          TABLE BODY
      ===================================== */}
 <div
        style={{
          overflowX: "auto",
        }}
      >
        <div
          style={{
            background: "#fff",
            border: "1px solid #f3f4f6",
            borderTop: "none",
            minWidth: 1440,
          }}
        >


        {loading ? (

          <div
            style={{
              padding: 20,
            }}
          >
            Loading...
          </div>

        ) : filteredTrips.length ===
          0 ? (

          <div
            style={{
              padding: 20,
              color: "#888",
            }}
          >
            No leads found
          </div>

        ) : (

          filteredTrips.map((trip) => (

            <div
              key={trip._id}

              style={{
                display: "grid",

gridTemplateColumns:
"90px 240px 180px 140px 120px 140px 180px 130px 220px",

minWidth:1440,

                padding: "14px 12px",

                borderBottom:
                  "1px solid #f3f4f6",

                cursor: "pointer",

                alignItems:
                  "flex-start",
              }}

              onClick={() =>
                navigate(
                  `/trips/${trip._id}`
                )
              }
            >

              {/* LEAD ID */}

              <div
                style={{
                  fontWeight: 700,
                  color: "#2563eb",
                  fontSize: 13,
                }}
              >
                {trip.leadId
                  ?.split("-")
                  ?.pop()

                  ||

                  trip._id?.slice(-6)}
              </div>

              {/* CUSTOMER */}

              <div>

                <div
                  style={{
                    fontWeight: 600,
                    color: "#111827",
                    marginBottom: 2,
                  }}
                >
                  {trip.customerName ||
                    "Unnamed"}
                </div>

                <div
                  style={{
                    fontSize: 12,
                    color: "#6b7280",
                  }}
                >
                  {trip.phone || "-"}
                </div>

                <div
                  style={{
                    fontSize: 12,
                    color: "#9ca3af",
                    marginTop: 2,
                  }}
                >
                  {trip.email || "-"}
                </div>

              </div>

              {/* DESTINATION */}

              <div>

                <div
                  style={{
                    fontWeight: 500,
                  }}
                >

{
  trip.destinationSnapshot ||
  trip.destination?.name ||
  (
    typeof trip.destination === "string"
      ? trip.destination
      : "-"
  )
}

                </div>

                <div
                  style={{
                    fontSize: 12,
                    color: "#9ca3af",
                    marginTop: 3,
                  }}
                >
                  Priority:{" "}

                  {trip.priority ||
                    "medium"}
                </div>

              </div>

              {/* PAX */}

<div
  style={{
    fontSize: 13,
    color: "#374151",
    fontWeight: 500,
    whiteSpace: "nowrap",
  }}
>
  A: {trip.pax?.adults ?? 0}

  {" | "}

  K: {trip.pax?.children ?? 0}

  {" | "}

  CNB: {trip.pax?.infants ?? 0}
</div>

              {/* STATUS */}

              <div>

                <span
                  style={{
                    background:
                      "#eff6ff",

                    color: "#2563eb",

                    padding:
                      "4px 8px",

                    borderRadius: 20,

                    fontSize: 12,

                    fontWeight: 600,

                    textTransform:
                      "capitalize",
                  }}
                >
                  {trip.status ||
                    "-"}
                </span>

              </div>

{/* ADVISOR */}

<div
  style={{
    fontSize: 13,
  }}
>
  {
    trip?.assignedTo?.name ||
    trip?.createdBy?.name ||
    "Unassigned"
  }
</div>

              {/* TAGS */}

              <div>

                {(trip.tags || [])
                  .length === 0 ? (

                  <span
                    style={{
                      fontSize: 12,
                      color:
                        "#9ca3af",
                    }}
                  >
                    No Tags
                  </span>

                ) : (

                  (trip.tags || [])
                    .slice(0, 3)

                    .map((tag, i) => (

                      <span
                        key={i}

                        style={{
                          display:
                            "inline-block",

                          fontSize: 11,

                          padding:
                            "4px 8px",

                          background:
                            "#eef2ff",

                          color:
                            "#4338ca",

                          borderRadius:
                            20,

                          marginRight: 4,

                          marginBottom: 4,
                        }}
                      >
                        {tag}
                      </span>
                    ))
                )}

              </div>
           

              {/* TRAVEL DATE */}

              <div
                style={{
                  fontSize: 12,
                  color: "#374151",
                }}
              >

{trip.travelDate
  ? new Date(
      trip.travelDate
    ).toLocaleDateString(
      "en-GB",
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }
    )
  : "-"}

              </div>


{/* LAST FOLLOW UP */}

<div
  style={{
    fontSize: 12,
    color: "#374151",
    lineHeight: 1.5,
  }}
>
  {trip.lastFollowUp ? (
    <>
      <div>
        {new Date(
          trip.lastFollowUp.date
        ).toLocaleDateString(
          "en-GB",
          {
            day: "2-digit",
            month: "short",
            year: "numeric",
          }
        )}
      </div>

      {trip.lastFollowUp.notes && (
        <div
          style={{
            color: "#6b7280",
            marginTop: 2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={trip.lastFollowUp.notes}
        >
          {trip.lastFollowUp.notes}
        </div>
      )}
    </>
  ) : (
    "-"
  )}
</div>


            </div>
          ))
        )}

      </div>

      {/* =====================================
          MODALS
      ===================================== */}

      <LeadFormModal
        visible={showLeadModal}

        onClose={() =>
          setShowLeadModal(
            false
          )
        }

        onSuccess={() => {

          setShowLeadModal(
            false
          );

          fetchTrips();
        }}
      />

      <BulkQueryImportModal
        visible={showImportModal}

        onClose={() =>
          setShowImportModal(
            false
          )
        }

        onSuccess={() => {

          setShowImportModal(
            false
          );

          fetchTrips();
        }}
      />

      </div>

     </div>

    </ResponsivePage>

  );
};

export default TripsPage;
